import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_PROJECT_COLOR,
  type Client,
  type Contact,
  type Metafield,
} from '@forecastify/shared';
import { Badge, Button, Dialog, Icon, Select, TextInput } from '@forecastify/ui';
import { MetafieldSection } from '../metafields/metafield-section';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { Field } from '../card';
import { ColorPicker } from './color-picker';
import { blankContact, isBlank, loadContacts, rowOf, type ContactRow } from './client-contacts';

/**
 * The Ügyfél dialog — mockup `markup.html` 2706–2760, transcribed field for field.
 *
 * ⚠️⚠️ **THE SHORT CODE IS GONE, COLUMN AND ALL** (`0104`). It was disabled on edit here, which
 * was the mockup's own control — and a disabled input still shows a value, which is what got it
 * reported: *"why is the client shortcode still appearing read only? we need to nuke that
 * completely, I don't want to see any leftover value."* Nothing was keyed on it.
 *
 * ⚠️ **Validation mirrors `createClientRequestSchema` and does not replace it.** Every message here
 * exists so somebody sees the problem before submitting; the server refuses the same things again.
 * A client-side check that the server does not repeat is not validation, it is a suggestion.
 */
export interface ClientDraft {
  name: string;
  contactName: string;
  contactEmail: string;
  countryCode: string;
  industry: string;
  color: string;
  /**
   * 21 Oct 2026 · the contacts, as rows — the first is the primary. `contactName` and
   * `contactEmail` above are set from it on submit, so the lists keep showing who to call.
   */
  contacts: ContactRow[];
  /** What the client had when the form opened — what `saveContacts` writes the difference against. */
  contactsBefore: readonly Contact[];
  /**
   * C10's values, keyed by the field's own key.
   *
   * ⚠️ Sent only when the workspace has defined fields for this entity — an empty object CLEARS
   * every value, so an absent one is what "leave them alone" looks like on the wire.
   */
  metafields: Record<string, unknown>;
}

/** The mockup's `cfCountryOptions`, its seven, in its order. */
const COUNTRIES = ['HU', 'DE', 'AT', 'SK', 'RO', 'GB', 'US'] as const;

/**
 * ⚠️ **The mockup's four industry seeds, and they are DATA rather than UI strings.**
 * `cfIndOptions` unions the industries already in use with `['Pénzügy', 'Ipar', 'Kereskedelem',
 * 'Közszféra']`, and whichever is picked is **stored on the client row**. Translating them would
 * store an English word for an English reader and a Hungarian one for a Hungarian reader — after
 * which the list's industry filter has two values for one industry and matches neither reliably.
 *
 * So the seeds stay Hungarian, exactly like the colour names. If industries ever need to be
 * translatable they need to become rows with ids, which is a schema decision and not a string one.
 */
const INDUSTRY_SEEDS = ['Pénzügy', 'Ipar', 'Kereskedelem', 'Közszféra'] as const;

export function ClientDialog({
  open,
  editing,
  existing,
  onClose,
  onSubmit,
  metafields = [],
  onArchive,
  serverError,
}: {
  open: boolean;
  /** The client being edited, or `null` for a new one. */
  editing: Client | null;
  /** Every client, so the industry list offers the ones already in use. */
  existing: readonly Client[];
  onClose: () => void;
  onSubmit: (draft: ClientDraft) => void;
  /** The workspace's `client` metafields. Empty when none are defined. */
  readonly metafields?: readonly Metafield[];
  /** Archive or restore. Absent for a new client, which has nothing to archive. */
  onArchive?: () => void;
  /** A refusal the server produced, already translated. */
  serverError: string | null;
}) {
  const t = useT();
  const [draft, setDraft] = useState<ClientDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  /* ⚠️ Reset when the dialog opens, not on every render: a draft that survived a close would
   * reappear in the next client's form, which reads as the wrong record being edited. */
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing === null) {
      setDraft(emptyDraft());
      return;
    }
    /* The client's own contact, as the first row, until its saved contacts arrive. */
    const seeded = blankContact({
      name: editing.contactName ?? '',
      email: editing.contactEmail ?? '',
    });
    setDraft({
      name: editing.name,
      contactName: editing.contactName ?? '',
      contactEmail: editing.contactEmail ?? '',
      contacts: [seeded],
      contactsBefore: [],
      countryCode: editing.countryCode,
      industry: editing.industry,
      color: editing.color,
      metafields: editing.metafields,
    });
    let live = true;
    void loadContacts(sessionRef.current, editing.id).then((contacts) => {
      if (!live || contacts === null || contacts.length === 0) return;
      setDraft((current) => ({
        ...current,
        contacts: contacts.map(rowOf),
        contactsBefore: contacts,
      }));
    });
    return () => {
      live = false;
    };
  }, [open, editing]);

  const patch = (part: Partial<ClientDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  const industries = [...new Set([...existing.map((client) => client.industry), ...INDUSTRY_SEEDS])]
    .filter((industry) => industry.length > 0)
    .sort((a, b) => a.localeCompare(b, 'hu'));

  const submit = (): void => {
    /* The mockup's own order and its own messages, so the same input produces the same sentence. */
    if (draft.name.trim().length === 0) {
      setError(t('clients.error.name'));
      return;
    }
    const filled = draft.contacts.filter((row) => !isBlank(row));
    if (filled.some((row) => row.name.trim() === '')) {
      setError(t('clients.error.contactName'));
      return;
    }
    if (
      filled.some(
        (row) => row.email.trim() !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(row.email.trim()),
      )
    ) {
      setError(t('clients.error.email'));
      return;
    }
    if (draft.industry.trim().length === 0) {
      setError(t('clients.error.industry'));
      return;
    }
    const primary = filled[0];
    onSubmit({
      ...draft,
      contacts: filled,
      contactName: primary?.name.trim() ?? '',
      contactEmail: primary?.email.trim() ?? '',
    });
  };

  const patchContact = (key: string, part: Partial<ContactRow>): void => {
    setDraft((current) => ({
      ...current,
      contacts: current.contacts.map((row) => (row.key === key ? { ...row, ...part } : row)),
    }));
    setError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={editing === null ? t('clients.dialog.new') : t('clients.dialog.edit')}
      description={editing === null ? t('clients.dialog.newHint') : t('clients.dialog.editHint')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3">
          <Field label={t('clients.field.name')}>
            <TextInput
              value={draft.name}
              onChange={(event) => {
                patch({ name: event.target.value });
              }}
              placeholder={t('clients.field.namePlaceholder')}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('clients.field.industry')}>
            <Select
              value={draft.industry}
              onChange={(event) => {
                patch({ industry: event.target.value });
              }}
              options={[
                { value: '', label: t('common.choose') },
                ...industries.map((industry) => ({ value: industry, label: industry })),
              ]}
            />
          </Field>
          <Field label={t('clients.field.country')}>
            <Select
              value={draft.countryCode}
              onChange={(event) => {
                patch({ countryCode: event.target.value });
              }}
              options={COUNTRIES.map((code) => ({
                value: code,
                /* One `t()` call per branch with a literal key — a computed key is invisible to
                 * anything that scans the source for catalogue usage. */
                label: `${countryName(t, code)} (${code})`,
              }))}
            />
          </Field>
        </div>

        <ColorPicker
          value={draft.color}
          onChange={(color) => {
            patch({ color });
          }}
          label={t('common.color')}
        />

        {/**
         * ⚠️⚠️ 21 Oct 2026 · **ONE PLACE FOR A CLIENT'S CONTACTS.** A *Kapcsolattartó* field on
         * this form and a *Kapcsolattartók* tab beside it held the same fact twice. A box like
         * *Metafieldek*: one row to start, `+` for more, the first is the primary.
         */}
        <section className="flex flex-col gap-2.5 rounded-xl border p-3.5">
          <div className="text-[12.5px] font-medium">{t('clients.contacts.title')}</div>
          {draft.contacts.map((row, index) => (
            <div key={row.key} className="flex flex-col gap-2">
              {index === 0 ? null : <div className="border-t" />}
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-[11.5px]">
                  {t('clients.contacts.row', { index: index + 1 })}
                </span>
                {index === 0 ? <Badge variant="secondary">{t('contacts.primary')}</Badge> : null}
                <div className="flex-1" />
                {draft.contacts.length > 1 ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('clients.contacts.remove', { index: index + 1 })}
                    onClick={() => {
                      setDraft((current) => ({
                        ...current,
                        contacts: current.contacts.filter((one) => one.key !== row.key),
                      }));
                    }}
                  >
                    <Icon name="x" size={14} />
                  </Button>
                ) : null}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <Field label={t('contacts.field.name')}>
                  <TextInput
                    value={row.name}
                    onChange={(event) => {
                      patchContact(row.key, { name: event.target.value });
                    }}
                    placeholder={t('contacts.field.namePlaceholder')}
                  />
                </Field>
                <Field label={t('contacts.field.email')}>
                  <TextInput
                    value={row.email}
                    onChange={(event) => {
                      patchContact(row.key, { email: event.target.value });
                    }}
                    placeholder="nev@ceg.hu"
                  />
                </Field>
                <Field label={t('contacts.field.phone')}>
                  <TextInput
                    value={row.phone}
                    onChange={(event) => {
                      patchContact(row.key, { phone: event.target.value });
                    }}
                  />
                </Field>
                <Field label={t('contacts.field.title')}>
                  <TextInput
                    value={row.title}
                    onChange={(event) => {
                      patchContact(row.key, { title: event.target.value });
                    }}
                    placeholder={t('contacts.field.titlePlaceholder')}
                  />
                </Field>
              </div>
            </div>
          ))}
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft((current) => ({
                  ...current,
                  contacts: [...current.contacts, blankContact()],
                }));
              }}
            >
              <Icon name="plus" size={13} />
              {t('clients.contacts.add')}
            </Button>
          </div>
        </section>

        {/* ⚠️ Its own box, below the product's own fields. Renders nothing when none are defined. */}
        <MetafieldSection
          fields={metafields}
          values={draft.metafields}
          /* ⚠️ A client has no member list to pick from — a `user` metafield on a client is
             offerable and simply has nobody to offer. The form renders it empty rather than
             pretending the field does not exist. */
          members={[]}
          onChange={(values) => {
            patch({ metafields: values });
          }}
        />

        {error === null && serverError === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </div>
        )}

        <div className="mt-1 flex items-center gap-2">
          {editing !== null && onArchive !== undefined ? (
            /* ⚠️ A ghost button, not a filled destructive one. `design-system/readme.md` reserves
             * the filled danger button for irreversible financial deletions; archiving is neither
             * irreversible nor a deletion, and the loudest control on this dialog is Save. */
            <Button variant="ghost" size="lg" className="text-destructive" onClick={onArchive}>
              {editing.archivedAt === null
                ? t('clients.action.archive')
                : t('clients.action.restore')}
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {editing === null ? t('clients.action.create') : t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function emptyDraft(): ClientDraft {
  return {
    name: '',
    contactName: '',
    contactEmail: '',
    contacts: [blankContact()],
    contactsBefore: [],
    countryCode: 'HU',
    industry: '',
    /* The mockup opens on `PROJ_COLORS[0]`. */
    color: DEFAULT_PROJECT_COLOR,
    metafields: {},
  };
}

/** One `t()` per branch, literal keys — see the note at the call site. */
function countryName(t: ReturnType<typeof useT>, code: (typeof COUNTRIES)[number]): string {
  if (code === 'HU') return t('country.HU');
  if (code === 'DE') return t('country.DE');
  if (code === 'AT') return t('country.AT');
  if (code === 'SK') return t('country.SK');
  if (code === 'RO') return t('country.RO');
  if (code === 'GB') return t('country.GB');
  return t('country.US');
}
